import { DATE_KINDS, ID_KINDS, NUMBER_KINDS, STRING_KINDS, } from "./schema.js";
export class ResolveError extends Error {
    span;
    constructor(message, span) {
        super(message);
        this.span = span;
        this.name = "ResolveError";
    }
}
const BUILTIN_ATTRS = new Set([
    "project",
    "reporter",
    "tag",
    "watcher",
    "created",
    "updated",
    "resolved",
    "number",
    "summary",
    "description",
]);
const BUILTIN_SORTS = new Set(["created", "updated", "resolved", "summary", "number", "project"]);
export function resolve(q, schema) {
    const r = new Resolver(schema);
    return {
        where: q.where ? r.expr(q.where) : { kind: "true" },
        sort: q.sort.map((s) => r.sort(s)),
    };
}
const DAY = 86_400_000;
class Resolver {
    s;
    constructor(s) {
        this.s = s;
    }
    expr(e) {
        switch (e.kind) {
            case "and":
                return { kind: "and", items: e.items.map((x) => this.expr(x)) };
            case "or":
                return { kind: "or", items: e.items.map((x) => this.expr(x)) };
            case "text":
                return { kind: "text", text: e.text };
            case "shortcut":
                return this.shortcut(e.name, e.span);
            case "attr":
                return this.attr(e.name, e.values, e.span);
        }
    }
    sort(c) {
        const lower = c.attr.toLowerCase();
        if (BUILTIN_SORTS.has(lower))
            return { key: { kind: "builtin", attr: lower }, dir: c.dir };
        const field = this.s.fieldByName(c.attr);
        if (!field)
            throw new ResolveError(`unknown field ${JSON.stringify(c.attr)}`, c.span);
        return { key: { kind: "field", field }, dir: c.dir };
    }
    shortcut(name, span) {
        const lower = name.toLowerCase();
        if (lower === "unresolved")
            return { kind: "resolved", value: false };
        if (lower === "resolved")
            return { kind: "resolved", value: true };
        if (lower === "my" || lower === "my issues" || lower === "me") {
            const assignee = this.s.fieldByName("Assignee");
            if (!assignee)
                throw new ResolveError("no Assignee field", span);
            return { kind: "field-in", field: assignee, ids: [this.s.currentUserId] };
        }
        // Bare enum value: unique across all bundle-backed fields.
        const hits = [];
        for (const f of this.s.allFields()) {
            if (f.bundleId === undefined)
                continue;
            for (const el of this.s.elementsOf(f.bundleId)) {
                if (el.name.toLowerCase() === lower)
                    hits.push({ field: f, el });
            }
        }
        if (hits.length === 1)
            return { kind: "field-in", field: hits[0].field, ids: [hits[0].el.id] };
        if (hits.length > 1) {
            // Same element name in several fields: any of them. YouTrack does the same.
            return { kind: "or", items: hits.map((h) => ({ kind: "field-in", field: h.field, ids: [h.el.id] })) };
        }
        const tag = this.s.tagByName(name);
        if (tag)
            return { kind: "tag-in", ids: [tag.id] };
        throw new ResolveError(`unknown shortcut #${name}`, span);
    }
    attr(name, values, span) {
        const lower = name.toLowerCase();
        const positives = values.filter((v) => !v.negated);
        const negatives = values.filter((v) => v.negated);
        const one = (v) => this.attrValue(lower, name, v, span);
        const items = [];
        if (positives.length === 1)
            items.push(one(positives[0]));
        else if (positives.length > 1)
            items.push({ kind: "or", items: positives.map(one) });
        for (const v of negatives)
            items.push({ kind: "not", pred: one(v) });
        return items.length === 1 ? items[0] : { kind: "and", items };
    }
    attrValue(lower, name, v, span) {
        // YouTrack's shorthands: `by: me` is the reporter, `for: me` the Assignee field.
        if (lower === "by")
            return this.builtinValue("reporter", v, span);
        if (lower === "for") {
            if (!this.s.fieldByName("Assignee"))
                throw new ResolveError('"for" needs an Assignee field', span);
            return this.attrValue("assignee", "Assignee", v, span);
        }
        if (BUILTIN_ATTRS.has(lower))
            return this.builtinValue(lower, v, span);
        const field = this.s.fieldByName(name);
        if (!field)
            throw new ResolveError(`unknown attribute ${JSON.stringify(name)}`, span);
        if (v.kind === "word") {
            if (isEmptyWord(v.text))
                return { kind: "field-empty", field };
            if (ID_KINDS.has(field.kind))
                return { kind: "field-in", field, ids: [this.idValue(field, v.text, v.span)] };
            if (NUMBER_KINDS.has(field.kind)) {
                const n = parseNumber(field, v.text, v.span);
                return { kind: "field-num", field, min: n, max: n };
            }
            if (DATE_KINDS.has(field.kind)) {
                const [from, to] = this.dateWord(v.text, v.span);
                return { kind: "field-date", field, from, to };
            }
            if (STRING_KINDS.has(field.kind))
                return { kind: "field-str", field, text: v.text };
            throw new ResolveError(`cannot filter ${field.name} by a value`, v.span);
        }
        // range
        if (NUMBER_KINDS.has(field.kind)) {
            return {
                kind: "field-num",
                field,
                min: v.from === null ? null : parseNumber(field, v.from, v.span),
                max: v.to === null ? null : parseNumber(field, v.to, v.span),
            };
        }
        if (DATE_KINDS.has(field.kind)) {
            const from = v.from === null ? null : this.dateWord(v.from, v.span)[0];
            const to = v.to === null ? null : this.dateWord(v.to, v.span)[1];
            return { kind: "field-date", field, from, to };
        }
        throw new ResolveError(`${field.name} does not support ranges`, v.span);
    }
    builtinValue(attr, v, span) {
        switch (attr) {
            case "project": {
                if (v.kind !== "word")
                    throw new ResolveError("project does not support ranges", v.span);
                const p = this.s.projectByName(v.text);
                if (!p)
                    throw new ResolveError(`unknown project ${JSON.stringify(v.text)}`, v.span);
                return { kind: "project-in", ids: [p.id] };
            }
            case "reporter": {
                if (v.kind !== "word")
                    throw new ResolveError("reporter does not support ranges", v.span);
                return { kind: "reporter-in", ids: [this.userId(v.text, v.span)] };
            }
            case "watcher": {
                if (v.kind !== "word")
                    throw new ResolveError("watcher does not support ranges", v.span);
                return { kind: "watcher-in", ids: [this.userId(v.text, v.span)] };
            }
            case "tag": {
                if (v.kind !== "word")
                    throw new ResolveError("tag does not support ranges", v.span);
                if (v.text.toLowerCase() === "star")
                    return { kind: "watcher-in", ids: [this.s.currentUserId] };
                const t = this.s.tagByName(v.text);
                if (!t)
                    throw new ResolveError(`unknown tag ${JSON.stringify(v.text)}`, v.span);
                return { kind: "tag-in", ids: [t.id] };
            }
            case "created":
            case "updated":
            case "resolved": {
                if (v.kind === "word") {
                    const [from, to] = this.dateWord(v.text, v.span);
                    return { kind: "builtin-date", attr, from, to };
                }
                return {
                    kind: "builtin-date",
                    attr,
                    from: v.from === null ? null : this.dateWord(v.from, v.span)[0],
                    to: v.to === null ? null : this.dateWord(v.to, v.span)[1],
                };
            }
            case "number": {
                if (v.kind === "word") {
                    const n = parseInt10(v.text, v.span);
                    return { kind: "number-range", min: n, max: n };
                }
                return {
                    kind: "number-range",
                    min: v.from === null ? null : parseInt10(v.from, v.span),
                    max: v.to === null ? null : parseInt10(v.to, v.span),
                };
            }
            case "summary":
            case "description": {
                if (v.kind !== "word")
                    throw new ResolveError(`${attr} does not support ranges`, v.span);
                return { kind: "text", text: v.text };
            }
        }
        throw new ResolveError(`unknown attribute ${attr}`, span);
    }
    idValue(field, text, span) {
        if (field.kind === "user")
            return this.userId(text, span);
        if (field.kind === "group") {
            // Groups resolve through the same name table as users for now.
            return this.userId(text, span);
        }
        const els = field.bundleId === undefined ? [] : this.s.elementsOf(field.bundleId);
        const lower = text.toLowerCase();
        const el = els.find((e) => e.name.toLowerCase() === lower);
        if (!el)
            throw new ResolveError(`${JSON.stringify(text)} is not a value of ${field.name}`, span);
        return el.id;
    }
    userId(text, span) {
        if (text.toLowerCase() === "me")
            return this.s.currentUserId;
        const u = this.s.userByName(text);
        if (!u)
            throw new ResolveError(`unknown user ${JSON.stringify(text)}`, span);
        return u.id;
    }
    /** A date word denotes a whole day: returns [startOfDay, endOfDay] in UTC. */
    dateWord(text, span) {
        const lower = text.toLowerCase();
        const today = Math.floor(this.s.now / DAY) * DAY;
        if (lower === "today")
            return [today, today + DAY - 1];
        if (lower === "yesterday")
            return [today - DAY, today - 1];
        if (lower === "tomorrow")
            return [today + DAY, today + 2 * DAY - 1];
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
        if (m) {
            const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            if (Number.isNaN(t))
                throw new ResolveError(`bad date ${text}`, span);
            return [t, t + DAY - 1];
        }
        throw new ResolveError(`bad date ${JSON.stringify(text)}`, span);
    }
}
function isEmptyWord(t) {
    const l = t.toLowerCase();
    return l === "empty" || l === "unassigned" || l === "unset" || l === "none";
}
function parseInt10(text, span) {
    if (!/^-?\d+$/.test(text))
        throw new ResolveError(`expected an integer, got ${JSON.stringify(text)}`, span);
    return Number(text);
}
/** Periods are minutes: `90`, `1h`, `1h 30m` (in braces), `2d` (8h days), `1w` (5 days). */
function parseNumber(field, text, span) {
    if (field.kind === "period") {
        const re = /(\d+)\s*([wdhm])/gi;
        let total = 0;
        let matched = false;
        for (const m of text.matchAll(re)) {
            matched = true;
            const n = Number(m[1]);
            switch (m[2].toLowerCase()) {
                case "w":
                    total += n * 5 * 8 * 60;
                    break;
                case "d":
                    total += n * 8 * 60;
                    break;
                case "h":
                    total += n * 60;
                    break;
                case "m":
                    total += n;
                    break;
            }
        }
        if (matched)
            return total;
        if (/^\d+$/.test(text))
            return Number(text);
        throw new ResolveError(`bad period ${JSON.stringify(text)}`, span);
    }
    if (field.kind === "integer")
        return parseInt10(text, span);
    if (!/^-?\d+(\.\d+)?$/.test(text))
        throw new ResolveError(`expected a number, got ${JSON.stringify(text)}`, span);
    return Number(text);
}
//# sourceMappingURL=resolve.js.map